import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  ColorMode, LookId, DEFAULT_LOOK, isLookId, effectiveMode, getLook,
  currentUserIdFromStorage, readBrowserCopy, lookKey, modeKey,
} from '../lib/looks';

// ─── Background looks ───────────────────────────────────────────────────────
// The eight looks (twelve backgrounds) live in src/lib/looks.ts; the gradients
// themselves live in src/styles/looks.css. This file only decides WHICH look and
// mode are active, applies them to <html>, and saves the choice per user.

// ─── Sidebar styles ─────────────────────────────────────────────────────────

export interface SidebarStyle {
  id: string;
  label: string;
  bg: string;
  border: string;
  matchesBody?: boolean;
}

export const SIDEBAR_STYLES: SidebarStyle[] = [
  {
    id: 'glass',
    label: 'Glass',
    bg: 'rgba(0,0,0,0.20)',
    border: 'rgba(255,255,255,0.15)',
  },
  {
    id: 'solid-dark',
    label: 'Solid Dark',
    bg: '#1a2035',
    border: 'rgba(255,255,255,0.08)',
  },
  {
    id: 'match-bg',
    label: 'Match BG',
    bg: 'transparent',
    border: 'rgba(255,255,255,0.10)',
    matchesBody: true,
  },
];

// ─── Light / dark colour mode (simple version, Lis 2026-08-06) ────────────────

export type { ColorMode };

// ─── Interface density ──────────────────────────────────────────────────────
/** Scales the --p-* spacing tokens + table/card padding via html[data-density].
 *  'comfortable' is the shipped default (no override applied). */
export type InterfaceDensity = 'comfortable' | 'compact' | 'spacious';

export const DENSITIES: InterfaceDensity[] = ['comfortable', 'compact', 'spacious'];

// ─── Context ────────────────────────────────────────────────────────────────

interface ThemeContextType {
  /** The background look (Tide, Dusk, …). Applied as `html[data-look]`. */
  look: LookId;
  setLook: (id: LookId) => void;
  /** True when the current look is one of the four coloured looks that set
   *  their own mode — the top-bar sun/moon is switched off for those. */
  lookForcesMode: boolean;
  /** Set when saving/loading the choice to the account failed. The choice is
   *  still kept in this browser. Null when all is well. */
  appearanceError: string | null;
  sidebarStyle: SidebarStyle;
  setSidebarStyle: (style: SidebarStyle) => void;
  /** Light/dark colour mode actually shown — swaps the --p-* tokens via
   *  `html[data-theme='light']` (pulse-theme.css). On a coloured look this is
   *  decided by the look; setColorMode then does nothing. */
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  /** Dock icon size, per person. Rides the SAME localStorage mechanism the colour
   *  mode uses — see DOCK_SIZES for why the values are fixed pixels. */
  dockSize: DockSizeId;
  setDockSize: (id: DockSizeId) => void;
  /** Interface density — same localStorage mechanism; applied via html[data-density]. */
  density: InterfaceDensity;
  setDensity: (d: InterfaceDensity) => void;
  /** Motion toggles (Settings → Appearance → Motion & Animations). Either
   *  animations OFF or reduce-motion ON reduces motion, and the OS
   *  prefers-reduced-motion setting combines with them — any one is enough. */
  animationsEnabled: boolean;
  setAnimationsEnabled: (on: boolean) => void;
  reduceMotion: boolean;
  setReduceMotion: (on: boolean) => void;
  /** The combined verdict (in-app toggles OR the OS setting). Consumers that gate
   *  JS-driven motion (the dock magnify) read THIS, never the raw toggles. */
  motionReduced: boolean;
}

/**
 * Dock sizes — FIXED PIXELS, deliberately.
 *
 * Not vw/vh: a viewport unit makes the dock grow with the window, so the tiles
 * drift away from whatever size the person actually picked and turn enormous on a
 * wide monitor. A chosen size should stay the size that was chosen.
 *
 * The magnify multiplies these, so hover behaves identically at every setting.
 */
export type DockSizeId = 'compact' | 'small' | 'medium' | 'comfortable' | 'large';

export const DOCK_SIZES: { id: DockSizeId; label: string; px: number }[] = [
  { id: 'compact',     label: 'Compact',     px: 32 },
  { id: 'small',       label: 'Small',       px: 36 },
  { id: 'medium',      label: 'Medium',      px: 44 },
  { id: 'comfortable', label: 'Comfortable', px: 48 },
  { id: 'large',       label: 'Large',       px: 52 },
];

export const DEFAULT_DOCK_SIZE: DockSizeId = 'medium';

/** The tile's glyph scales with the tile, so a Compact dock isn't a big icon in a
 *  small box. Ratio taken from the shipped 48px tile / 26px glyph. */
export const dockGlyphPx = (basePx: number) => Math.round(basePx * (26 / 48));

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  /* ⛔ THE FIRST RENDER IS THE SAME AS THE FIRST PAINT. Both read THIS PERSON's browser
     copy (`cbops_look:<id>`), the person being whoever the stored session says — the
     pre-paint script in public/index.html does exactly this before any bundle runs. A
     browser copy is never painted for anyone but the person it belongs to; nobody
     signed in, or no copy for this person, is Tide/dark.

     The old shared keys (`cbops_look`, `pulsehq_color_mode`) are no longer read: they
     were one copy for whoever used the browser, which is how a second person was shown
     the first person's look. The old 'pulsehq_bg_theme_v2' key is not read either. */
  const [initial] = useState(() => {
    const uid = currentUserIdFromStorage();
    return { uid, ...readBrowserCopy(uid) };
  });
  const [look, setLookState] = useState<LookId>(initial.look ?? DEFAULT_LOOK);
  /* Whose browser copy `look`/`userMode` are written to. Null while signed out. */
  const [copyFor, setCopyFor] = useState<string | null>(initial.uid);

  const [sidebarStyle, setSidebarStyleState] = useState<SidebarStyle>(() => {
    const saved = localStorage.getItem('pulsehq_sidebar_style');
    return SIDEBAR_STYLES.find(s => s.id === saved) ?? SIDEBAR_STYLES[0];
  });

  /* The person's OWN light/dark choice. It is remembered even while a coloured
     look is showing (those set their own mode), so going back to a paired look
     restores it. The mode actually shown is `colorMode` below. */
  const [userMode, setUserModeState] = useState<ColorMode>(initial.mode ?? 'dark');
  const colorMode = effectiveMode(look, userMode);
  const lookForcesMode = !!getLook(look).forcedMode;

  const [appearanceError, setAppearanceError] = useState<string | null>(null);
  /* Who the account read says is signed in (null until it answers). */
  const userIdRef = useRef<string | null>(null);
  const loadedForRef = useRef<string | null>(null);
  /* Whose look is on screen right now — starts as the stored session's person. */
  const shownForRef = useRef<string | null>(initial.uid);
  /* ⛔ A PICK WINS. Set by setLook/setColorMode; while it is set the account read that
     is (or was) in flight for this person may not overwrite the screen. */
  const pickedRef = useRef(false);
  /* A pick made before anyone was confirmed signed in: saved once they are. */
  const pendingSaveRef = useRef(false);
  /* The latest values, for the async paths above, which would otherwise close over a
     stale render. */
  const lookRef = useRef(look);
  const userModeRef = useRef(userMode);
  lookRef.current = look;
  userModeRef.current = userMode;

  /* Same shape as the colour mode above — seeded from localStorage on first render
     so the dock paints at the chosen size immediately, with no flash of the default. */
  const [dockSize, setDockSizeState] = useState<DockSizeId>(() => {
    const saved = localStorage.getItem('pulsehq_dock_size');
    return DOCK_SIZES.some(s => s.id === saved) ? (saved as DockSizeId) : DEFAULT_DOCK_SIZE;
  });

  const [density, setDensityState] = useState<InterfaceDensity>(() => {
    const saved = localStorage.getItem('pulsehq_density');
    return DENSITIES.includes(saved as InterfaceDensity) ? (saved as InterfaceDensity) : 'comfortable';
  });

  const [animationsEnabled, setAnimationsEnabledState] = useState<boolean>(
    () => localStorage.getItem('pulsehq_animations') !== 'off',
  );
  const [reduceMotion, setReduceMotionState] = useState<boolean>(
    () => localStorage.getItem('pulsehq_reduce_motion') === 'on',
  );

  /* The OS accessibility setting, live-subscribed so toggling it takes effect
     without a reload (moved here from Topbar so the app has ONE combined
     motion verdict instead of per-consumer matchMedia reads). */
  const [osReducedMotion, setOsReducedMotion] = useState<boolean>(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setOsReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /* Any one source reducing motion is enough — the in-app toggles never
     override the OS accessibility request, only add to it. */
  const motionReduced = reduceMotion || !animationsEnabled || osReducedMotion;

  /* Look + mode ride two attributes on <html>; looks.css and pulse-theme.css
     key off them. Layout effect so a change never paints one frame wrong. */
  useLayoutEffect(() => {
    document.documentElement.dataset.look = look;
  }, [look]);

  useEffect(() => {
    localStorage.setItem('pulsehq_sidebar_style', sidebarStyle.id);
  }, [sidebarStyle]);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = colorMode;
  }, [colorMode]);

  /* The browser copy, PER PERSON — what the next page load paints first. Written for
     the person whose look is on screen, and for nobody while signed out. */
  useEffect(() => {
    if (!copyFor) return;
    try {
      localStorage.setItem(lookKey(copyFor), look);
      localStorage.setItem(modeKey(copyFor), userMode);
    } catch { /* storage unavailable: the account copy still holds the choice */ }
  }, [copyFor, look, userMode]);

  /* ── Saving to the account (table user_appearance, one row per person) ──
     ThemeProvider sits ABOVE AuthProvider in App.tsx, so it cannot use
     useAuth(); it asks the Supabase client who is signed in instead. Failures
     are shown (appearanceError), never swallowed; the choice stays in this
     browser either way. */
  const persist = useCallback(async (lookId: LookId, mode: ColorMode) => {
    const uid = userIdRef.current;
    if (!uid) { pendingSaveRef.current = true; return; }
    try {
      const { error } = await supabase
        .from('user_appearance')
        .upsert(
          { user_id: uid, look: lookId, mode, updated_at: new Date().toISOString() },
          { onConflict: 'user_id' },
        );
      if (error) throw error;
      setAppearanceError(null);
    } catch {
      setAppearanceError('Could not save your look to your account. It is kept in this browser for now.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadFor = async (uid: string) => {
      if (loadedForRef.current === uid) return;
      loadedForRef.current = uid;
      try {
        const { data, error } = await supabase
          .from('user_appearance')
          .select('look, mode')
          .eq('user_id', uid)
          .maybeSingle();
        if (error) throw error;
        if (cancelled || userIdRef.current !== uid) return;
        /* ⛔ A PICK MADE WHILE THIS READ WAS IN FLIGHT WINS. The pick is already on
           screen and already saved; applying this older answer would put the previous
           look back on screen while the account holds the new one. */
        if (pickedRef.current) return;
        if (!data) return; // no row yet: keep this person's browser copy (or Tide)
        /* Another device changed the look since this browser's copy was written: this
           is the ONE switch, and it happens as the page opens. */
        if (isLookId(data.look)) setLookState(data.look);
        if (data.mode === 'light' || data.mode === 'dark') setUserModeState(data.mode);
        setAppearanceError(null);
      } catch {
        // Allow a retry (next sign-in event or reload) instead of marking this user loaded.
        if (loadedForRef.current === uid) loadedForRef.current = null;
        if (!cancelled) setAppearanceError('Could not load your saved look from your account. Using this browser\'s copy.');
      }
    };
    const onUser = (uid: string | null) => {
      /* A DIFFERENT PERSON than the one whose look is showing — a sign-in, a sign-out,
         or a session the pre-paint could not read. Never keep the previous person's
         look: show this person's browser copy, or Tide/dark, and start their picks
         afresh. Signed out is Tide/dark. */
      if (uid !== shownForRef.current) {
        const copy = readBrowserCopy(uid);
        shownForRef.current = uid;
        pickedRef.current = false;
        pendingSaveRef.current = false;
        setLookState(copy.look ?? DEFAULT_LOOK);
        setUserModeState(copy.mode ?? 'dark');
        setCopyFor(uid);
      }
      userIdRef.current = uid;
      if (!uid) { loadedForRef.current = null; return; }
      /* A pick made before the session was confirmed is saved now, once. */
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        void persist(lookRef.current, userModeRef.current);
      }
      void loadFor(uid);
    };
    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!cancelled) onUser(data.session?.user?.id ?? null);
      } catch {
        if (!cancelled) setAppearanceError('Could not check who is signed in, so your look is not being saved to your account.');
      }
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      onUser(session?.user?.id ?? null);
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
    /* ⚠️ NO CROSS-TAB SYNC, ON PURPOSE. Listening for another tab's `storage` event
       would change this tab's look with no click in it — the exact complaint this
       fixes. A look picked in one tab reaches another tab on its next load. */
  }, [persist]);

  /* The dock reads its base size from a CSS variable on the root, so the tiles size
     themselves in CSS rather than every tile carrying an inline width. */
  useEffect(() => {
    const px = DOCK_SIZES.find(s => s.id === dockSize)?.px ?? 44;
    document.documentElement.style.setProperty('--pq-dock-base', `${px}px`);
    document.documentElement.style.setProperty('--pq-dock-glyph', `${dockGlyphPx(px)}px`);
    localStorage.setItem('pulsehq_dock_size', dockSize);
  }, [dockSize]);

  /* Density rides an html attribute so index.css can rescale the --p-* spacing
     tokens + table/card padding in one place ('comfortable' = no override). */
  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem('pulsehq_density', density);
  }, [density]);

  /* One attribute carries the combined motion verdict; index.css keys its
     global transition/animation kill-switch off it. The raw toggles persist
     separately so each survives reload on its own. */
  useEffect(() => {
    document.documentElement.dataset.motion = motionReduced ? 'reduced' : 'full';
    localStorage.setItem('pulsehq_animations', animationsEnabled ? 'on' : 'off');
    localStorage.setItem('pulsehq_reduce_motion', reduceMotion ? 'on' : 'off');
  }, [motionReduced, animationsEnabled, reduceMotion]);

  const setLook = (id: LookId) => {
    pickedRef.current = true;
    setLookState(id);
    void persist(id, userMode);
  };
  const setSidebarStyle     = (style: SidebarStyle)     => setSidebarStyleState(style);
  const setColorMode = (mode: ColorMode) => {
    if (lookForcesMode) return; // a coloured look decides its own mode
    pickedRef.current = true;
    setUserModeState(mode);
    void persist(look, mode);
  };
  const setDockSize         = (id: DockSizeId)          => setDockSizeState(id);
  const setDensity          = (d: InterfaceDensity)     => setDensityState(d);
  const setAnimationsEnabled = (on: boolean)            => setAnimationsEnabledState(on);
  const setReduceMotion     = (on: boolean)             => setReduceMotionState(on);

  return (
    <ThemeContext.Provider
      value={{
        look, setLook, lookForcesMode, appearanceError,
        sidebarStyle, setSidebarStyle,
        colorMode, setColorMode,
        dockSize, setDockSize,
        density, setDensity,
        animationsEnabled, setAnimationsEnabled,
        reduceMotion, setReduceMotion,
        motionReduced,
      }}
    >
      {/* The one fixed background: picture layer, then an (empty) tint layer.
          Behind every module, never scrolls. See src/styles/looks.css. */}
      <div className="cb-bg" aria-hidden="true" />
      <div className="cb-bg-tint" aria-hidden="true" />
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
